import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { ComparisonData, sampleData, calculateMetrics } from '@/lib/mock-market-data';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface PortfolioComparisonChartProps {
  data: ComparisonData;
  fundName: string;
  isTactical: boolean;
  isHistorical: boolean;
  fullscreen?: boolean;
}

// Format currency
const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

// Format percentage
const formatPercent = (value: number) => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${(value * 100).toFixed(1)}%`;
};

// Format date for display - show year only
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.getFullYear().toString();
};

// Get unique year ticks from chart data - returns dates representing each unique year
const getUniqueYearTicks = (data: { date: string }[]): string[] => {
  if (!data || data.length === 0) return [];

  const yearsSeen = new Set<number>();
  const ticks: string[] = [];

  for (const point of data) {
    const year = new Date(point.date).getFullYear();
    if (!yearsSeen.has(year)) {
      yearsSeen.add(year);
      ticks.push(point.date);
    }
  }

  return ticks;
};

// Custom tooltip component
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  // Sort by value descending
  const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

  // Determine colors based on rank
  const getColor = (index: number) => {
    if (index === 0) return 'text-status-good';
    if (index === sortedPayload.length - 1) return 'text-status-critical';
    return 'text-status-warning';
  };

  const getIcon = (index: number) => {
    if (index === 0) return <TrendingUp className="h-3.5 w-3.5" />;
    if (index === sortedPayload.length - 1) return <TrendingDown className="h-3.5 w-3.5" />;
    return <Minus className="h-3.5 w-3.5" />;
  };

  return (
    <Card className="bg-popover border shadow-lg">
      <CardContent className="p-3 space-y-2">
        <p className="text-xs text-muted-foreground font-medium">
          {formatDate(label)}
        </p>
        <div className="space-y-1.5">
          {sortedPayload.map((entry: any, index: number) => (
            <div
              key={entry.dataKey}
              className={cn('flex items-center justify-between gap-4 text-sm', getColor(index))}
            >
              <div className="flex items-center gap-1.5">
                {getIcon(index)}
                <span className="font-medium">{entry.name}</span>
              </div>
              <span className="font-mono font-semibold">
                {formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function PortfolioComparisonChart({
  data,
  fundName,
  isTactical,
  isHistorical,
  fullscreen = false,
}: PortfolioComparisonChartProps) {
  // Sample data for performance
  const chartData = useMemo(() => {
    const userSampled = sampleData(data.userPortfolio, 200);
    const benchmarkSampled = sampleData(data.benchmark, 200);
    const tacticalSampled = sampleData(data.tacticalFund, 200);

    return userSampled.map((point, i) => ({
      date: point.date,
      userPortfolio: point.value,
      benchmark: benchmarkSampled[i]?.value ?? 0,
      tacticalFund: tacticalSampled[i]?.value ?? 0,
    }));
  }, [data]);

  // Calculate metrics for each series
  const metrics = useMemo(() => ({
    user: calculateMetrics(data.userPortfolio),
    benchmark: calculateMetrics(data.benchmark),
    tactical: calculateMetrics(data.tacticalFund),
  }), [data]);

  // Determine which is performing best at end
  const getBestPerformer = () => {
    const values = [
      { name: 'Your Portfolio', value: metrics.user.finalValue },
      { name: 'S&P 500', value: metrics.benchmark.finalValue },
      { name: fundName, value: metrics.tactical.finalValue },
    ];
    return values.sort((a, b) => b.value - a.value)[0].name;
  };

  const startValue = data.userPortfolio[0]?.value ?? 100000;

  // Fullscreen mode - just the chart filling the container
  if (fullscreen) {
    return (
      <div className="h-full w-full relative">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 40, right: 40, left: 20, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />

            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              ticks={getUniqueYearTicks(chartData)}
            />

            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              domain={['auto', 'auto']}
              width={80}
            />

            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="top"
              height={50}
              formatter={(value) => (
                <span className="text-sm font-medium">{value}</span>
              )}
            />

            {/* Reference line at baseline */}
            <ReferenceLine
              y={startValue}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              strokeOpacity={0.4}
              label={{
                value: 'Baseline',
                position: 'left',
                fontSize: 11,
                fill: 'hsl(var(--muted-foreground))',
              }}
            />

            {/* Your Portfolio */}
            <Line
              type="monotone"
              dataKey="userPortfolio"
              name="Your Portfolio"
              stroke="hsl(var(--primary))"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 8, strokeWidth: 2 }}
            />

            {/* S&P 500 Benchmark */}
            <Line
              type="monotone"
              dataKey="benchmark"
              name="S&P 500"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 8, strokeWidth: 2 }}
            />

            {/* Tactical/Selected Fund */}
            <Line
              type="monotone"
              dataKey="tacticalFund"
              name={fundName}
              stroke={isTactical ? 'hsl(var(--status-good))' : 'hsl(var(--status-warning))'}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 8, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Default mode with metric cards below
  return (
    <div className="space-y-4">
      {/* Chart */}
      <div className="h-[400px] sm:h-[500px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />

            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ className: 'stroke-border' }}
              ticks={getUniqueYearTicks(chartData)}
            />

            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={{ className: 'stroke-border' }}
              domain={['auto', 'auto']}
              width={70}
            />

            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="top"
              height={36}
              formatter={(value) => (
                <span className="text-sm">{value}</span>
              )}
            />

            {/* Reference line at baseline */}
            <ReferenceLine
              y={startValue}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 5"
              strokeOpacity={0.5}
              label={{
                value: 'Baseline',
                position: 'left',
                fontSize: 10,
                fill: 'hsl(var(--muted-foreground))',
              }}
            />

            {/* Your Portfolio */}
            <Line
              type="monotone"
              dataKey="userPortfolio"
              name="Your Portfolio"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />

            {/* S&P 500 Benchmark */}
            <Line
              type="monotone"
              dataKey="benchmark"
              name="S&P 500"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />

            {/* Tactical/Selected Fund */}
            <Line
              type="monotone"
              dataKey="tacticalFund"
              name={fundName}
              stroke={isTactical ? 'hsl(var(--status-good))' : 'hsl(var(--status-warning))'}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Metrics Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Your Portfolio */}
        <Card className="border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Your Portfolio</span>
              <Badge variant="outline" className="text-xs">
                {isHistorical ? 'Hypothetical' : 'Projected'}
              </Badge>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-mono font-bold">
                {formatCurrency(metrics.user.finalValue)}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className={cn(
                  metrics.user.totalReturn >= 0 ? 'text-status-good' : 'text-status-critical'
                )}>
                  {formatPercent(metrics.user.totalReturn)} total
                </span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">
                  {formatPercent(metrics.user.cagr)} CAGR
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Max drawdown: {formatPercent(-metrics.user.maxDrawdown)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* S&P 500 */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">S&P 500</span>
              <Badge variant="secondary" className="text-xs">Benchmark</Badge>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-mono font-bold">
                {formatCurrency(metrics.benchmark.finalValue)}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className={cn(
                  metrics.benchmark.totalReturn >= 0 ? 'text-status-good' : 'text-status-critical'
                )}>
                  {formatPercent(metrics.benchmark.totalReturn)} total
                </span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">
                  {formatPercent(metrics.benchmark.cagr)} CAGR
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Max drawdown: {formatPercent(-metrics.benchmark.maxDrawdown)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tactical/Selected Fund */}
        <Card className={cn(isTactical && 'border-status-good/30')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium truncate">{fundName}</span>
              <Badge
                variant={isTactical ? 'default' : 'secondary'}
                className={cn('text-xs', isTactical && 'bg-status-good')}
              >
                {isTactical ? 'Tactical' : 'Index'}
              </Badge>
            </div>
            <div className="space-y-1">
              <p className="text-2xl font-mono font-bold">
                {formatCurrency(metrics.tactical.finalValue)}
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span className={cn(
                  metrics.tactical.totalReturn >= 0 ? 'text-status-good' : 'text-status-critical'
                )}>
                  {formatPercent(metrics.tactical.totalReturn)} total
                </span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">
                  {formatPercent(metrics.tactical.cagr)} CAGR
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Max drawdown: {formatPercent(-metrics.tactical.maxDrawdown)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Best performer callout */}
      <div className="text-center text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{getBestPerformer()}</span>
        {' '}is the best performer in this {isHistorical ? 'historical period' : 'projection'}
      </div>
    </div>
  );
}

